import React, { useState } from 'react';
import { Container } from '@amzn/awsui-components-react-v3';
import { Region, StackedEntry } from './types';
import { REGION_COLORS } from './constants';

interface StackedBarsProps {
    header: React.ReactNode;
    entries: StackedEntry[]; // full, uncollapsed; collapse happens here so Others can expand
    threshold: number;
    regionsPresent: Region[];
}

/** Horizontal bars grouped by a dimension, stacked/colored by region. Tail (≤ threshold)
 *  collapses into a click-to-expand "Others" row. */
const StackedBars: React.FC<StackedBarsProps> = ({ header, entries, threshold, regionsPresent }) => {
    const [showOthers, setShowOthers] = useState(false);

    const over = entries.filter((e) => e.total > threshold);
    const under = entries.filter((e) => e.total <= threshold).sort((a, b) => b.total - a.total);

    const others: StackedEntry | null = under.length
        ? under.reduce(
              (acc, e) => {
                  acc.total += e.total;
                  for (const [reg, c] of Object.entries(e.byRegion)) {
                      acc.byRegion[reg as Region] = (acc.byRegion[reg as Region] || 0) + (c || 0);
                  }
                  return acc;
              },
              { key: `Others (${under.length} ≤ ${threshold})`, total: 0, byRegion: {} } as StackedEntry,
          )
        : null;

    const peak = Math.max(...over.map((e) => e.total), others ? others.total : 0, 1);

    const bar = (e: StackedEntry, opts: { indent?: boolean; toggle?: boolean } = {}) => (
        <div
            className="sw-stack__row"
            key={(opts.indent ? 'sub:' : '') + e.key}
            onClick={opts.toggle ? () => setShowOthers((v) => !v) : undefined}
            onKeyDown={opts.toggle ? (ev) => { if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); setShowOthers((v) => !v); } } : undefined}
            role={opts.toggle ? 'button' : undefined}
            tabIndex={opts.toggle ? 0 : undefined}
            aria-expanded={opts.toggle ? showOthers : undefined}
            style={{ ...(opts.toggle ? { cursor: 'pointer' } : {}), ...(opts.indent ? { paddingLeft: 18, opacity: 0.85 } : {}) }}
        >
            <div className="sw-stack__label" title={e.key}>
                {opts.toggle ? (showOthers ? '▼ ' : '▶ ') : ''}
                {e.key}
            </div>
            <div className="sw-stack__track">
                {regionsPresent.map((reg) => {
                    const c = e.byRegion[reg] || 0;
                    if (!c) return null;
                    return (
                        <div
                            key={reg}
                            className="sw-stack__seg"
                            title={`${reg}: ${c.toLocaleString()}`}
                            style={{ width: `${(100 * c) / peak}%`, background: REGION_COLORS[reg] || '#888' }}
                        />
                    );
                })}
            </div>
            <div className="sw-stack__count">{e.total.toLocaleString()}</div>
        </div>
    );

    return (
        <Container header={header}>
            <div className="sw-legend">
                {regionsPresent.map((r) => (
                    <span className="sw-legend__item" key={r}>
                        <span className="sw-legend__dot" style={{ background: REGION_COLORS[r] || '#888' }} />
                        {r}
                    </span>
                ))}
            </div>
            <div className="sw-stack">
                {over.length === 0 && !others ? (
                    <div className="sw-bars__empty">No rows match filters</div>
                ) : (
                    <>
                        {over.map((e) => bar(e))}
                        {others && bar(others, { toggle: true })}
                        {others && showOthers && under.map((e) => bar(e, { indent: true }))}
                    </>
                )}
            </div>
        </Container>
    );
};

export default StackedBars;
